import { Collapse, styled } from '@ori-ui/mui';
import type { FC, ReactNode, RefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationKeys } from '../models';

interface StyledMoreProps {
  ownerState: {
    isExpanded: boolean;
  };
}

const StyledMore = styled('div')<StyledMoreProps>(({ ownerState: { isExpanded }, theme }) => ({
  background: theme.palette.common.white,
  bottom: 0,
  fontWeight: theme.typography.fontWeightBold,
  marginTop: 0,
  minWidth: 'calc(100% - 20px)',
  position: 'relative',
  textAlign: 'center',
  transition: 'margin-top 300ms ease',
  width: '100%',
  '&:after': {
    backgroundImage: `linear-gradient(rgba(255, 255, 255, 0), ${theme.palette.common.white})`,
    bottom: '100%',
    content: '""',
    display: 'block',
    height: '20px',
    left: 0,
    position: 'absolute',
    width: '100%',
  },
  ...(isExpanded && {
    marginTop: theme.spacing(5),
  }),
}));

const StyledMoreLabel = styled('div')({
  cursor: 'pointer',
  display: 'inline-block',
});

const MAX_CONTENT_HEIGHT = 57;

export const ExpandableContent: FC<{ children: ReactNode }> = ({ children }) => {
  const contentRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [translate] = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasLongDescription, setHasLongDescription] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prevState) => !prevState);
  }, []);

  useEffect(() => {
    const descriptionHeight = contentRef.current?.scrollHeight;
    if (descriptionHeight !== undefined && descriptionHeight > MAX_CONTENT_HEIGHT) {
      setHasLongDescription(true);
    }
  }, []);

  return (
    <>
      <Collapse
        ref={contentRef}
        in={isExpanded}
        collapsedSize={57}
      >
        {children}
      </Collapse>
      {hasLongDescription ? (
        <StyledMore ownerState={{ isExpanded }}>
          <StyledMoreLabel onClick={toggleExpanded}>
            {translate(isExpanded ? TranslationKeys.readLess : TranslationKeys.readMore)}
          </StyledMoreLabel>
        </StyledMore>
      ) : null}
    </>
  );
};
